import * as AppConstants from "../../constant/app-constant";
import { BreakpointData } from "../../helper/breakpoint/breakpoint";
import { titleCase } from "../../helper/text";
import { getCookie, getCookieKeyValue } from "@directv-digital/dds.services.cookie-service";
import { cloneDeep } from "lodash";
import { logError } from "../../helper/logger";


declare global {
  interface Window {
    DataMappingInterface: any;
  }
}

declare const window: any;

// Generated by Copilot- Start - 2026-04-03 14:13:17
/**
 * Returns the GTM dataLayer array, always assigning it back to window so queued
 * events are not lost if GTM loads after this call. When running inside an iframe
 * we try the parent's dataLayer first and fall back to the current window on any
 * cross-origin SecurityError.
 * Returns an inert empty array in non-browser (SSR) environments so this module
 * is safe to import server-side.
 */
function getDataLayer(): any[] {
  if (typeof window === 'undefined') {
    // SSR / non-browser context — return a no-op array so callers don't crash
    return [];
  }
  try {
    if (window !== window.parent) {
      window.parent.dataLayer = window.parent.dataLayer || [];
      return window.parent.dataLayer;
    }
  } catch (_e) {
    // cross-origin iframe — fall through to current window
  }
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}
// Generated by Copilot- End - 2026-04-03 14:13:17

enum EventActions {
  PAGE_LOAD = "pageLoad",
  LINK_CLICK = "linkClick",
  FORM_RESPONSE = "formResponse",
  FORM_SUBMIT = "formSubmit",
  IMPRESSION = "impression",
  SYSTEM_EVENT = "systemEvent",
  DTV_Timeout_Warning_System_Submit = "DTV_Timeout_Warning_System_Submit",
  DTV_Timeout_Logout_System_Submit = "DTV_Timeout_Logout_System_Submit",
  Dispute_Promo_Eligibility_Submit = "Dispute_Promo_Eligibility_Submit",
}

enum EventCode {
  PAGE_LOAD = "Page_Load",
  LINK_CLICK = "Link_Click",
  FORM_RESPONSE = "Form_Response",
  DTV_ZipCode_Submit = "DTV_ZipCode_Submit",
  FORM_SUBMIT = "Form_Submit",
  IMPRESSION = "impression",
  DTV_SUPPORT_SEARCH_TEXT_SUBMIT = "DTV_Support_Search_Text_Submit",
  DTV_Timeout_Warning_System_Submit = "DTV_Timeout_Warning_System_Submit",
  DTV_Timeout_Logout_System_Submit = "DTV_Timeout_Logout_System_Submit",
  Dispute_Promo_Eligibility_Submit = "Dispute_Promo_Eligibility_Submit",
  SYSTEM_EVENT = "SYSTEM_EVENT",
}

export  class ReportingService {
  private sentImpressions: any = {};

  constructFullUrl(path: string): string {
    if (path !== undefined && path.indexOf("http") !== -1) {
      return path;
    }
    return window.location.origin + path;
  }

  fireLinkClickEvent(additionaldata: any, useAsync = false): void {
    if (!additionaldata || Object.keys(additionaldata).length <= 0) {
      return;
    }
    if (additionaldata.linkDestination) {
      additionaldata.linkDestination = this.constructFullUrl(
        additionaldata.linkDestination
      );
    }
    if (useAsync)
      Promise.resolve(
        this.fireEvent(
          EventActions.LINK_CLICK,
          EventCode.LINK_CLICK,
          additionaldata
        )
      );
    else
      this.fireEvent(
        EventActions.LINK_CLICK,
        EventCode.LINK_CLICK,
        additionaldata
      );
  }

  fireFormAction(
    additionaldata: any,
    eventAction: string,
    eventCode: string
  ): void {
    if (!additionaldata || Object.keys(additionaldata).length <= 0) {
      return;
    }
    if (additionaldata.linkDestination) {
      additionaldata.linkDestination = this.constructFullUrl(
        additionaldata.linkDestination
      );
    }
    this.fireEvent(
      EventActions[eventAction],
      EventCode[eventCode],
      additionaldata
    );
  }
  firePageLoadEvent(additionaldata: any): void {
    this.fireEvent(EventActions.PAGE_LOAD, EventCode.PAGE_LOAD, additionaldata);
  }

  fireImpression(additionaldata: any): void {
    this.fireEvent(
      EventActions.IMPRESSION,
      EventCode.IMPRESSION,
      additionaldata
    );
  }

  fixAnalyticsValues(
    data: any,
    analyticsValue: BreakpointData,
    layoutName: string,
    layoutId: number,
    order?: number,
    cms_type = "AEM"
  ): any {
    const updatedData = cloneDeep(data);
    const updatedAnalyticsValue = cloneDeep(analyticsValue);
    try {
      if (!updatedData) {
      return undefined;
      }
      let newData = { ...updatedData };
      if (updatedData && !updatedData.analytics) {
      newData = {
        ...updatedData,
        ...{ analytics: updatedAnalyticsValue || {} },
      };
      }
      if (newData.analytics && layoutName && layoutId > -1) {
      if (typeof newData.analytics === `object`) {
        newData.analytics.contentSystem = cms_type;
        newData.analytics.componentOrder = layoutId + 1;
        newData.analytics.slotOrder = order ? order + 1 : 1;
        newData.analytics.componentName = layoutName;
        delete newData.analytics.contentPageSectionId;
        delete newData.analytics.contentId;
        delete newData.analytics.slotFriendlyName;
      }
      }
      return newData;
    } catch (error) {
      logError("ReportingService", error, { method: "fixAnalyticsValues" });
      return data;
    }
  }

  sendImpressions(
    dataArray: any,
    breakpoint: number,
    pageName: string,
    pagePath: string,
    root: string,
    reqQueryParams: any
  ) { 
    const impression: any = [];
    if (!dataArray || !dataArray.data) {
      return;
    }
    dataArray.data.map((data, key) => {
      if (data && data.props) {
        const dataObj = data.props;
        const uniqueId = pageName + data.props.options?.id + key;
        if (
          dataObj.analytics &&
          dataObj["type"] !== "CARDGRIDLIST" &&
          dataObj["type"] !== "CAROUSEL"
        ) {
          if (!this.sentImpressions[uniqueId] && !dataObj.hc) {
            impression.push(dataObj.analytics);
            this.sentImpressions[uniqueId] = 1;
          }
        }
        for (const index in dataObj) {
          if (dataObj[index]) {
            const currentData = dataObj[index];

            // Logic to iterate over parallax container items and gather impressions.
            if (index === "layout" && dataObj["type"] === "PARALLAXCONTAINER") {
              const parallaxItems = dataObj["items"];
              currentData.forEach((itemName: any, i: number) => {
                const itemData = parallaxItems[itemName];
                if (itemData) {
                  // Fires off Canvas impression analytics.
                  // NOTE: As per discussion with analytics team, we are only expected to fire Canvas impressions
                  //       for canvases that have interactive elements (buttons/links).
                  if (itemData.analytics && itemData.type === "CANVAS") {
                    if (
                      !this.sentImpressions[uniqueId + index + i] &&
                      !itemData.hc
                    ) {
                      impression.push(itemData.analytics);
                      this.sentImpressions[uniqueId + index + i] = 1;
                    }
                  }
                  // NOTE: As per discussion with analytics team, we are expected to fire impression data for each card
                  //       in a CardGridList. We do not need to fire an impression for the CardGridList container though.
                  else if (itemData.type === "CARDGRIDLIST") {
                    const rows = itemData.rows || [];
                    rows.forEach((rowData) => {
                      const cards = rowData.cards || [];
                      cards.forEach((cardData, cardIndex) => {
                        if (
                          !this.sentImpressions[
                            uniqueId + index + "cardgridlist" + cardIndex
                          ] &&
                          !itemData.hc
                        ) {
                          impression.push({
                            ...cardData.analytics,
                            componentOrder: i + 1,
                            slotOrder: cardIndex + 1,
                          });
                          this.sentImpressions[
                            uniqueId + index + "cardgridlist" + cardIndex
                          ] = 1;
                        }
                      });
                    });
                  }
                  // NOTE: As per discussion with analytics team, we are expected to fire impression data for each card
                  //       in a Carousel. We do not need to fire an impression for the Carousel container though.
                  else if (itemData.type === "CAROUSEL") {
                    const rows = itemData.rows || {};
                    const cards = rows.cards || [];
                    cards.forEach((cardData, cardIndex) => {
                      if (
                        !this.sentImpressions[
                          uniqueId + index + "carousel" + cardIndex
                        ] &&
                        !itemData.hc
                      ) {
                        impression.push({
                          ...cardData.analytics,
                          componentOrder: i + 1,
                          slotOrder: cardIndex + 1,
                        });
                        this.sentImpressions[
                          uniqueId + index + "carousel" + cardIndex
                        ] = 1;
                      }
                    });
                  }
                }
              });
            } else if (currentData instanceof Array) {
              currentData.forEach((item: any, i: number) => {
                if (item?.analytics) {
                  if (!this.sentImpressions[uniqueId + index + i] && !item.hc) {
                    impression.push(item.analytics);
                    this.sentImpressions[uniqueId + index + i] = 1;
                  }
                }
                if (item?.cards && Array.isArray(item?.cards)) {
                  item.cards.forEach((subItem: any, j: number) => {
                    if (subItem?.analytics) {
                      if (dataObj.type === "CARDGRIDLIST") {
                        if (
                          !this.sentImpressions[
                            uniqueId + index + "cardgridlist" + j
                          ] &&
                          !subItem.hc
                        ) {
                          impression.push({
                            ...subItem.analytics,
                            componentOrder: key + 1,
                            slotOrder: j + 1,
                          });
                          this.sentImpressions[
                            uniqueId + index + "cardgridlist" + j
                          ] = 1;
                        }
                      } else if (dataObj.type === "CAROUSEL") {
                        if (
                          !this.sentImpressions[
                            uniqueId + index + "carousel" + j
                          ] &&
                          !subItem.hc
                        ) {
                          impression.push({
                            ...subItem.analytics,
                            componentOrder: i + 1,
                            slotOrder: j + 1,
                          });
                          this.sentImpressions[
                            uniqueId + index + "carousel" + j
                          ] = 1;
                        }
                      }
                    }
                  });
                }
              });
            } else if (currentData.analytics) {
              if (!this.sentImpressions[uniqueId + index] && !currentData.hc) {
                impression.push(currentData.analytics);
                this.sentImpressions[uniqueId + index] = 1;
              }
            }
          }
        }
      }
    });
    let pageUrl = `${pagePath.replace(root, "").replace(/\/+$/g, "")}/`;
    const chluPageUrl = pageUrl.split("?")[0];
    if (pageUrl.includes("channel-lineup")) {
      if (reqQueryParams.tab === "rsn") {
        pageUrl =
          chluPageUrl.charAt(chluPageUrl.length - 1) === "/"
            ? `${chluPageUrl}regional-sports-local`
            : `${chluPageUrl}/regional-sports-local`;
      } else {
        pageUrl =
          chluPageUrl.charAt(chluPageUrl.length - 1) === "/"
            ? `${chluPageUrl}national`
            : `${chluPageUrl}/national`;
      }
    }
    const noSlash = `${pageName.replace(/\//g, " ")}`;
    if (this.sentImpressions.page !== pagePath) {
      let pageImprData: any =
        dataArray.feed && dataArray.feed.pageProperties
          ? dataArray.feed.pageProperties.pageImpressionAnalytics
          : null;
      let LineOfBusiness = AppConstants.LINE_OF_BUSINESS;
      let iXPTestName = `VideoUpperFunnel ${noSlash} - SSR`;
      let iXPAssignmentName: any;
      let abSystem = AppConstants.CONTENT_SYSTEM;
      if (dataArray.feed.analytics && dataArray.feed.analytics.iXPTestName) {
        iXPTestName = dataArray.feed.analytics.iXPTestName;
      }
      if (
        dataArray.feed.analytics &&
        dataArray.feed.analytics.iXPAssignmentName
      ) {
        iXPAssignmentName = dataArray.feed.analytics.iXPAssignmentName;
      }
      if (dataArray.feed.analytics && dataArray.feed.analytics.abSystem) {
        abSystem = dataArray.feed.analytics.abSystem;
      }
      if (dataArray.feed.analytics && dataArray.feed.analytics.lineOfBusiness) {
        LineOfBusiness = dataArray.feed.analytics.lineOfBusiness;
      }
      if (pageUrl && pageUrl.charAt(0) == "?") {
        pageUrl = "/";
      }
      const pageData: any = {
        "page.category.applicationName": AppConstants.APPLICATION_NAME,
        "page.category.pageFunction":
          pageName === `homepage` ? `Home Page` : AppConstants.PAGE_FUNCTION,
        "page.category.siteSection":
          pageName === `homepage` ? `att` : AppConstants.SITE_SECTION,
        "page.location.url": pageUrl,
        "page.pageInfo.friendlyPageName":
          pageName === `homepage`
            ? `UF Home Pg`
            : `UF ${titleCase(noSlash)} Pg`,
        "page.category.pageOwnership": AppConstants.OWNERSHIP,
        // get the value from feed otherwise General;
        "page.pageInfo.lineOfBusiness": LineOfBusiness,
        "page.pageInfo.pageTitle": document.title,
      };
      pageData["page.pageInfo.flowCode"] = AppConstants.FLOW_CODE;
      dataArray.data.map((data) => {
        if (data && data.type === "INPAGE_LOCATION") {
          const cookie = getCookie("attPersistantLocalization");
          const cookieZipCode: string = getCookieKeyValue(cookie, "zipCode")
            ? getCookieKeyValue(cookie, "zipCode")
            : getCookieKeyValue(cookie, "zip");
          const zipFromProps = dataArray.zipCode
            ? dataArray.zipCode.toString()
            : "";
          pageData["page.pageInfo.zipCode"] = cookieZipCode || zipFromProps;
        }
      });

      if (pageImprData) {
        delete pageImprData.variationID;
      } else {
        pageImprData = {
          abCampaignName: AppConstants.CONTENT_PAGE_SECTION_ID,
          abSystem,
          abTestEvent: AppConstants.SLOT_FRIENDLY_NAME,
          abTestId: iXPTestName,
          abTreatmentName: iXPAssignmentName,
        };
      }
      pageImprData["page.pageInfo.flowCode"] = AppConstants.FLOW_CODE;

      // Updates for DTV Home/Stream/Satellite Landers.
      if (
        pageName === "homepage" ||
        pageName === "stream" ||
        pageName === "satellite"
      ) {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.location.domain"] = window.location.origin;
        pageData["page.location.url"] = window.location.pathname;
        pageData["page.pageInfo.flowCode"] = dataArray.isUserAuthenticated
          ? "Existing"
          : "Prospect";
        if (pageName === "homepage" && dataArray.recognizedUserData) {
          if (dataArray.recognizedUserData.accountType === "stream")
            pageData["page.location.url"] = "/stream/recognized/";
          else if (dataArray.recognizedUserData.accountType === "satellite")
            pageData["page.location.url"] = "/satellite/recognized/";
        }
      }

      if (pageName === "sitemap") {
        pageData["page.category.applicationName"] = "Video-Support";
        pageData["page.location._addressBarFullURL"] = window.location.href;
        pageData["page.location.domain"] = window.location.origin;
        pageData["page.location.url"] = window.location.pathname;
        pageData["page.pageInfo.flowCode"] = dataArray.isUserAuthenticated
          ? "Existing"
          : "Prospect";
      }

      // Setting specific application name & OEM attribution value for Evergent to downstream applications for specific pages - Amazon firetv, Roku, Samsung & Googlefiber
      if (pageName === "stream/amazon") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMFIRETV";
      }
      if (pageName === "stream/roku") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMROKU";
      }
      if (pageName === "stream/samsung") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMSAMSUNG";
      }
      if (pageName === "stream/googlefiber") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMGOOGLE";
      }
      if (pageName === "stream/pointbroadband") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMPOINTBROADBAND";
      }
      if (pageName === "stream/ziplyfiber") {
        pageData["page.category.applicationName"] = "Video-Sales";
        pageData["page.attributes.offerType"] = "VIDEOOEMZIPLYFIBER";
      }
      if (pageName === "stream/mavs") {
        pageData["page.category.applicationName"] = "Video-Sales";
      }
      if (pageName === "tvpromise") {
        pageData["page.category.applicationName"] = "Video-Support";
      }

      this.firePageLoadEvent(pageData);
      this.fireImpression(pageImprData);
      this.sentImpressions.page = pagePath;
    }
    impression.forEach((item: any) => {
      this.fireImpression(item);
    });
  }

  tagCookieData(data: any, cookieString: string) {
    if (!data) {
      return;
    }
    let newData = { ...data };
    const cookieData = getCookie(cookieString);
    if (!cookieData) {
      return data;
    }
    newData = { ...data, inventorySpaceId: cookieData.split(`=`)[1] };
    return newData;
  }

  fireModalPageLoadEvent = (
    url,
    friendlyPageName,
    zipcode?,
    pageName?,
    msg?
  ) => {
    const pageData: any = {
      // "page.category.applicationName": AppConstants.APPLICATION_NAME,
      "page.category.applicationName":
        pageName === "tvpromise"
          ? "Video-Support"
          : AppConstants.APPLICATION_NAME,
      "page.category.pageFunction": AppConstants.PAGE_FUNCTION,
      "page.category.flowCode": AppConstants.FLOW_CODE,
      "page.location.url": url,
      "page.pageInfo.friendlyPageName": friendlyPageName,
      "page.pageInfo.lineOfBusiness": AppConstants.LINE_OF_BUSINESS,
      "page.pageInfo.pageTitle": document.title,
      "page.category.pageOwnership": AppConstants.OWNERSHIP,
      "page.attributes.displayedMessage": pageName === "tvpromise" ? msg : "",
    };
    if (zipcode) {
      pageData["page.pageInfo.zipCode"] = zipcode;
    }
    this.firePageLoadEvent(pageData);
  };

  private fireEvent(
    action: EventActions,
    code: EventCode,
    additionaldata: any
  ) {
    if (additionaldata && Object.keys(additionaldata).length > 0) {
      const addtnlData = {
        ...additionaldata,
      };
      let i = 0;
      if (!window.DataMappingInterface) {
        const ddoExist = setInterval(() => {
          i++;
          if (i > 100) {
            clearInterval(ddoExist);
          }
          if (process.browser && window.DataMappingInterface) {
            clearInterval(ddoExist);
            /*const payload = ddo.createPayload(action, code);

            if (addtnlData) {
              Object.keys(addtnlData).forEach((key) => {
                payload.addAdditionalDataItem(key, addtnlData[key]);
              });
            }
            ddo.track(payload);*/
            // ddo.transport.pushEvent(action, code, addtnlData); // Version 3.0 for SSAF
            window.DataMappingInterface.init({
              eventAction: action,
              eventCode: code,
              additionaldata: addtnlData,
              datatransform: false,
            }); // Version 2.0 SSAF
          }
        }, 50);
      } else {
        window.DataMappingInterface.init({
          eventAction: action,
          eventCode: code,
          additionaldata: addtnlData,
          datatransform: false,
        });
      }
    }
  }


  fireQuizEventGA = (data = null) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "quiz",
      ...data,
    };
    dataLayer.push(additionalData);
  };  

  fireVideoPlayEvent = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: data.event_name,
      video_status: data.video_status,
      video_name: data.video_name,
      video_id: data.video_id,
      video_length_total: data.video_length_total,
      video_length_viewed: data.video_length_viewed,
    };
    dataLayer.push(additionalData);
  };

  
  fireGa4ProductImpression = (ga4EventData: any) => {
    const dataLayer = getDataLayer();
    dataLayer.push({
      event: "view_item_list",
      currencyCode: "USD",
      items: ga4EventData,
    });
  };

  fireLeadCaptureEventGA = (data = null) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "lead_capture",
      ...data,
    };
    dataLayer.push(additionalData);
  };

  fireTVPromiseGA = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "tvpromise_displayed",
      displayed_message: data,
    };
    dataLayer.push(additionalData);
  };

  fireVideoPlayerGA = (data?) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      ...data,
    };
    dataLayer.push(additionalData);
  };

  fireCustomLinkClickGA = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "custom_link_click",
      linkName: data,
      linkPosition: "packageCarousel",
      linkDestination: "",
    };
    dataLayer.push(additionalData);
  };

  fireWidgetCustomLinkClickGA = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "custom_link_click",
      linkPosition: "chat",
      ...data,
    };
    dataLayer.push(additionalData);
  };

  fireErrorEvent= (errorType: string, url: string)=>{
    const dataLayer = getDataLayer();
    const eventData = {
        event: "error_page_view",
        error_type: errorType,
        url: url,
        timestamp: new Date().toISOString(),
    };
    dataLayer.push(eventData);
}

  fireTabHandleChange = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      'event': 'trackEvent',
      'event_name': 'custom_link_click',
      'linkName': data.linkName,
      'linkPosition': data.linkPosition,
      'linkDestination': data.linkDestination
    };
    dataLayer.push(additionalData);
  }
  
  fireSearchImpressions = (impressions) => {
    const dataLayer = getDataLayer();
    const additionalData = ({
      'event' : 'search',
      'searchTerm' : impressions,
      'searchCategory' : 'channelLineup'
    });
    dataLayer.push(additionalData);
  }
  fireCustomLinkClickImpressions = (impressions) => {
    const dataLayer = getDataLayer();
    const additionalData = ({
      'event' : 'trackEvent',
      'event_name' : 'custom_link_click',
      'linkName' : impressions,
      'linkPosition' : 'widget search'
      });
    dataLayer.push(additionalData);
  }
  fireCustomClickMultiSearchGA = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event: "trackEvent",
      event_name: "custom_link_click",
      linkName: data.toLowerCase(),
      linkPosition: "panel_channel lineup",
      linkDestination: window?.location?.href,
    };
    dataLayer.push(additionalData);
  };

  fireModifiedComponentTypePageLoadEvent = (data) => {
    const dataLayer = getDataLayer();
    const additionalData = {
      event : 'trackEvent',
      event_name : 'dynamic_component',
      dim_name : 'displayed',
      dim_value : data,
    }
    dataLayer.push(additionalData);
  }
}
